/**
 * useAuctionSounds — Web Audio API sound effects for auction events.
 * All sounds synthesised procedurally (no external files needed).
 */

import { useRef, useCallback, useEffect } from 'react';

type AuctionSoundType = 'winner_fanfare' | 'money_credited' | 'auction_ended' | 'new_bid';

export function useAuctionSounds(enabled = true) {
  const ctxRef = useRef<AudioContext | null>(null);

  const getCtx = useCallback((): AudioContext => {
    if (!ctxRef.current || ctxRef.current.state === 'closed') {
      ctxRef.current = new AudioContext();
    }
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  useEffect(() => {
    return () => { ctxRef.current?.close(); };
  }, []);

  // ─── Grand winner fanfare ────────────────────────────────────────────────────
  const playWinnerFanfare = useCallback((ctx: AudioContext) => {
    const now = ctx.currentTime;

    // Rising triumphant arpeggio: C4 → E4 → G4 → B4 → C5 → E5
    const notes = [261.63, 329.63, 392.0, 493.88, 523.25, 659.25];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + i * 0.1);

      // Slight vibrato for richness
      const vibLfo = ctx.createOscillator();
      vibLfo.frequency.value = 5;
      const vibGain = ctx.createGain();
      vibGain.gain.value = 3;
      vibLfo.connect(vibGain).connect(osc.frequency);
      vibLfo.start(now + i * 0.1);
      vibLfo.stop(now + i * 0.1 + 0.8);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now + i * 0.1);
      gain.gain.linearRampToValueAtTime(0.25, now + i * 0.1 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.85);

      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.1);
      osc.stop(now + i * 0.1 + 0.9);
    });

    // Bell-like overtone at peak
    const bell = ctx.createOscillator();
    bell.type = 'sine';
    bell.frequency.value = 1046.5; // C6
    const bellGain = ctx.createGain();
    bellGain.gain.setValueAtTime(0.3, now + 0.55);
    bellGain.gain.exponentialRampToValueAtTime(0.001, now + 1.8);
    bell.connect(bellGain).connect(ctx.destination);
    bell.start(now + 0.55);
    bell.stop(now + 1.9);

    // Coin shower celebration
    const coinCount = 10;
    for (let i = 0; i < coinCount; i++) {
      const t = now + 0.5 + i * 0.08 + Math.random() * 0.04;
      const pitch = 700 + Math.random() * 800;
      const coinOsc = ctx.createOscillator();
      coinOsc.type = 'sine';
      coinOsc.frequency.setValueAtTime(pitch, t);
      coinOsc.frequency.exponentialRampToValueAtTime(pitch * 0.5, t + 0.25);
      const coinGain = ctx.createGain();
      coinGain.gain.setValueAtTime(0.12, t);
      coinGain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
      coinOsc.connect(coinGain).connect(ctx.destination);
      coinOsc.start(t);
      coinOsc.stop(t + 0.3);
    }
  }, []);

  // ─── Money credited (cash register + coins) ──────────────────────────────────
  const playMoneyCredited = useCallback((ctx: AudioContext) => {
    const now = ctx.currentTime;

    // Cash register "ding" — sharp metallic transient
    const ringOsc = ctx.createOscillator();
    ringOsc.type = 'sine';
    ringOsc.frequency.setValueAtTime(1800, now);
    ringOsc.frequency.exponentialRampToValueAtTime(900, now + 0.4);
    const ringGain = ctx.createGain();
    ringGain.gain.setValueAtTime(0.4, now);
    ringGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    ringOsc.connect(ringGain).connect(ctx.destination);
    ringOsc.start(now);
    ringOsc.stop(now + 0.55);

    // Secondary harmonic for richness
    const ring2 = ctx.createOscillator();
    ring2.type = 'sine';
    ring2.frequency.setValueAtTime(2700, now);
    ring2.frequency.exponentialRampToValueAtTime(1350, now + 0.3);
    const ring2Gain = ctx.createGain();
    ring2Gain.gain.setValueAtTime(0.15, now);
    ring2Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    ring2.connect(ring2Gain).connect(ctx.destination);
    ring2.start(now);
    ring2.stop(now + 0.4);

    // Satisfying coin clink cascade
    for (let i = 0; i < 5; i++) {
      const t = now + 0.15 + i * 0.1;
      const pitch = 500 + i * 120;
      const coinOsc = ctx.createOscillator();
      coinOsc.type = 'sine';
      coinOsc.frequency.setValueAtTime(pitch, t);
      coinOsc.frequency.exponentialRampToValueAtTime(pitch * 0.6, t + 0.2);
      const coinGain = ctx.createGain();
      coinGain.gain.setValueAtTime(0.18, t);
      coinGain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
      coinOsc.connect(coinGain).connect(ctx.destination);
      coinOsc.start(t);
      coinOsc.stop(t + 0.25);
    }
  }, []);

  // ─── Auction ended chime ─────────────────────────────────────────────────────
  const playAuctionEnded = useCallback((ctx: AudioContext) => {
    const now = ctx.currentTime;
    // Three descending then resolved tones
    [523.25, 440, 392, 523.25].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.2, now + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.45);
    });
  }, []);

  // ─── New bid placed ──────────────────────────────────────────────────────────
  const playNewBid = useCallback((ctx: AudioContext) => {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(660, now);
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.08);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.2);
  }, []);

  // ─── Public API ──────────────────────────────────────────────────────────────
  const play = useCallback((sound: AuctionSoundType) => {
    if (!enabled) return;
    try {
      const ctx = getCtx();
      switch (sound) {
        case 'winner_fanfare': playWinnerFanfare(ctx); break;
        case 'money_credited': playMoneyCredited(ctx); break;
        case 'auction_ended': playAuctionEnded(ctx); break;
        case 'new_bid': playNewBid(ctx); break;
      }
    } catch (e) {
      console.warn('[AuctionSound] error:', e);
    }
  }, [enabled, getCtx, playWinnerFanfare, playMoneyCredited, playAuctionEnded, playNewBid]);

  return { play };
}
